import { useState } from "react";
import type {
  KnowledgeScope,
  KnowledgeTimeRange,
  KnowledgeTreeSource,
  SharePointSiteSummary,
  SharePointTreeItem,
} from "@yazamco/shared-types";
import { Icon } from "../../components/ui/Icon";
import { useKnowledgeSites, useKnowledgeTree } from "./hooks";
import { MicrosoftProductMark } from "./MicrosoftProductMark";

type PickerView = "scope" | "sites" | "tree" | "oneDrive" | "time";
type TimedSource = "mail" | "teams";

const TIME_LABELS: Record<KnowledgeTimeRange["preset"], string> = {
  hour: "Past hour",
  day: "Past day",
  week: "Past week",
  month: "Past month",
  year: "Past year",
  custom: "Custom range",
};

function timeRangeLabel(range: KnowledgeTimeRange): string {
  return TIME_LABELS[range.preset];
}

/**
 * How deep a chain of single folders opens itself.
 *
 * A level holding exactly one folder is a level with nothing to choose, so it
 * opens on its own and the walk lands where a decision first exists. The cap is
 * only there because each level is a request, and a drive built as
 * `Archive/2024/Q1/…` would otherwise walk itself to the bottom before showing
 * anything.
 */
const AUTO_EXPAND_DEPTH = 8;

export function scopeLabel(scope: KnowledgeScope): string {
  switch (scope.kind) {
    case "all": return "All Microsoft 365";
    case "mail": return `Email · ${timeRangeLabel(scope.timeRange)}`;
    case "teams": return `Teams · ${timeRangeLabel(scope.timeRange)}`;
    case "sharePoint": return "SharePoint · all sites";
    case "sharePointSite": return `SharePoint · ${scope.siteName ?? "selected site"}`;
    case "sharePointPath": return `SharePoint · ${scope.label ?? "folder"}`;
    case "oneDrive": return "OneDrive";
    case "oneDrivePath": return `OneDrive · ${scope.label ?? "folder"}`;
    case "externalItem": return "Copilot connectors";
    default: return "Microsoft 365";
  }
}

export interface KnowledgeScopePanelProps {
  value: KnowledgeScope;
  onChange: (scope: KnowledgeScope) => void;
  onClose: () => void;
}

/**
 * "Search in", drawn as the canvas draws it: a short radio list, and — for
 * SharePoint — a drill-in that replaces the list with a back arrow, a
 * breadcrumb and the folders at that level, closed by one footer button that
 * commits wherever you have walked to.
 *
 * The content only; the trigger and the placement belong to the composer.
 */
export function KnowledgeScopePanel({
  value,
  onChange,
  onClose,
}: KnowledgeScopePanelProps): JSX.Element {
  const [view, setView] = useState<PickerView>("scope");
  const [search, setSearch] = useState("");
  const [site, setSite] = useState<SharePointSiteSummary | null>(null);
  const [timedSource, setTimedSource] = useState<TimedSource>("mail");

  // Sites are fetched when the popover opens, but shown only after SharePoint
  // is chosen. That keeps the first list calm and the next step responsive.
  const sites = useKnowledgeSites(search, true);
  const retrievalUnavailable = sites.data?.retrievalEnabled === false;

  function closeWith(scope: KnowledgeScope): void {
    onChange(scope);
    onClose();
  }

  function chooseSite(selected: SharePointSiteSummary): void {
    setSite(selected);
    onChange({
      kind: "sharePointSite",
      siteId: selected.siteId,
      siteName: selected.displayName,
      siteUrl: selected.webUrl,
    });
    setView("tree");
  }

  if (view === "scope") {
    return (
      <>
        <div className="cp-pop-head">
          <strong>Search in</strong>
        </div>
        <div className="cp-pop-scroll">
          <ScopeRow
            label="All Microsoft 365"
            hint="Mail, calendar, chats and files"
            product="microsoft365"
            selected={value.kind === "all"}
            onPick={() => closeWith({ kind: "all" })}
          />
          <ScopeRow
            label="SharePoint"
            hint={retrievalUnavailable ? "SharePoint is not available here" : "A site, library or folder"}
            product="sharepoint"
            selected={value.kind.startsWith("sharePoint")}
            disabled={retrievalUnavailable}
            loading={sites.isLoading}
            drill
            onPick={() => setView("sites")}
          />
          <ScopeRow
            label="OneDrive"
            hint={retrievalUnavailable ? "OneDrive is not available here" : "Your own files, or one folder in them"}
            product="onedrive"
            selected={value.kind.startsWith("oneDrive")}
            disabled={retrievalUnavailable}
            loading={sites.isLoading}
            drill
            onPick={() => setView("oneDrive")}
          />
          <ScopeRow
            label="Email"
            hint="Outlook messages in a selected time range"
            product="outlook"
            selected={value.kind === "mail"}
            drill
            onPick={() => { setTimedSource("mail"); setView("time"); }}
          />
          <ScopeRow
            label="Teams"
            hint="Chats, channel messages and meetings in a selected time range"
            product="teams"
            selected={value.kind === "teams"}
            drill
            onPick={() => { setTimedSource("teams"); setView("time"); }}
          />
        </div>
      </>
    );
  }

  if (view === "time") {
    const current = value.kind === timedSource ? value.timeRange : { preset: "week" as const };
    return (
      <TimeRangePanel
        source={timedSource}
        value={current}
        onBack={() => setView("scope")}
        onChoose={(timeRange) => closeWith({ kind: timedSource, timeRange })}
      />
    );
  }

  if (view === "sites") {
    return (
      <>
        <DrillHeader crumb="SharePoint" onBack={() => setView("scope")} />
        <div className="cp-pop-search">
          <Icon name="search" size={14} />
          <input
            type="search"
            value={search}
            autoFocus
            aria-label="Search SharePoint sites"
            placeholder="Search sites…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="cp-pop-scroll">
          <DrillRow
            name="All SharePoint sites"
            onOpen={() => closeWith({ kind: "sharePoint" })}
          />
          {sites.isLoading && <p className="cp-pop-hint">Loading sites…</p>}
          {!sites.isLoading && (sites.data?.items.length ?? 0) === 0 && (
            <p className="cp-pop-hint">No sites matched your search.</p>
          )}
          {sites.data?.items.map((item) => (
            <DrillRow
              key={item.id}
              name={item.displayName}
              hasChildren
              onOpen={() => chooseSite(item)}
            />
          ))}
        </div>
      </>
    );
  }

  if (view === "oneDrive") {
    return (
      <>
        <DrillHeader crumb="OneDrive" onBack={() => setView("scope")} />
        <div className="cp-pop-scroll">
          <FolderTree
            source="oneDrive"
            treeLabel="OneDrive"
            emptyMessage="There are no folders in your OneDrive."
            onChoose={(item) =>
              closeWith({
                kind: "oneDrivePath",
                path: item.webUrl,
                driveId: item.driveId,
                itemId: item.id,
                label: item.name,
              })
            }
          />
        </div>
        <div className="cp-pop-foot">
          <button
            type="button"
            className="cp-pop-commit"
            onClick={() => closeWith({ kind: "oneDrive" })}
          >
            <Icon name="checkSmall" size={14} />
            Search in all of OneDrive
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <DrillHeader crumb={`SharePoint · ${site?.displayName ?? ""}`} onBack={() => setView("sites")} />
      <div className="cp-pop-scroll">
        {site && (
          <FolderTree
            source="sharePoint"
            siteId={site.id}
            treeLabel={site.displayName}
            emptyMessage="No document libraries are available."
            onChoose={(item) =>
              closeWith({
                kind: "sharePointPath",
                path: item.webUrl,
                siteId: site.siteId,
                driveId: item.driveId,
                itemId: item.id,
                label: `${site.displayName} · ${item.name}`,
              })
            }
          />
        )}
      </div>
      <div className="cp-pop-foot">
        <button
          type="button"
          className="cp-pop-commit"
          onClick={() =>
            site &&
            closeWith({
              kind: "sharePointSite",
              siteId: site.siteId,
              siteName: site.displayName,
              siteUrl: site.webUrl,
            })
          }
        >
          <Icon name="checkSmall" size={14} />
          Search in this site
        </button>
      </div>
    </>
  );
}

function ScopeRow({
  label,
  hint,
  product,
  selected,
  disabled,
  loading,
  drill,
  onPick,
}: {
  label: string;
  hint: string;
  product: "microsoft365" | "sharepoint" | "onedrive" | "outlook" | "teams";
  selected: boolean;
  disabled?: boolean;
  loading?: boolean;
  drill?: boolean;
  onPick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`cp-pop-row${selected ? " is-selected" : ""}`}
      title={hint}
      role="radio"
      aria-label={label}
      aria-checked={selected}
      aria-busy={loading || undefined}
      disabled={disabled}
      onClick={onPick}
    >
      <MicrosoftProductMark product={product} />
      <span className="cp-pop-row-copy"><strong>{label}</strong></span>
      {loading && <span className="cp-pop-row-loading" aria-hidden />}
      {drill && <Icon name="chevronRight" size={14} stroke={1.9} className="cp-pop-chevron" />}
      {!drill && selected && <Icon name="checkSmall" size={14} className="cp-pop-selected" />}
    </button>
  );
}

function TimeRangePanel({
  source,
  value,
  onBack,
  onChoose,
}: {
  source: TimedSource;
  value: KnowledgeTimeRange;
  onBack: () => void;
  onChoose: (range: KnowledgeTimeRange) => void;
}): JSX.Element {
  const toInput = (iso: string): string => {
    const date = new Date(iso);
    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  };
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1_000);
  const [from, setFrom] = useState(
    value.preset === "custom" ? toInput(value.from) : toInput(weekAgo.toISOString()),
  );
  const [to, setTo] = useState(
    value.preset === "custom" ? toInput(value.to) : toInput(now.toISOString()),
  );
  const valid = Boolean(from && to && new Date(from).getTime() <= new Date(to).getTime());

  return (
    <>
      <DrillHeader crumb={`${source === "mail" ? "Email" : "Teams"} · Time range`} onBack={onBack} />
      <div className="cp-time-presets" role="radiogroup" aria-label="Time range">
        {(["hour", "day", "week", "month", "year"] as const).map((preset) => (
          <button
            key={preset}
            type="button"
            role="radio"
            aria-checked={value.preset === preset}
            className={value.preset === preset ? "is-selected" : undefined}
            onClick={() => onChoose({ preset })}
          >
            {TIME_LABELS[preset]}
          </button>
        ))}
      </div>
      <div className="cp-time-custom">
        <strong>Custom range</strong>
        <label>
          <span>From</span>
          <input type="datetime-local" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          <span>To</span>
          <input type="datetime-local" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} />
        </label>
        <button
          type="button"
          className="cp-pop-commit"
          disabled={!valid}
          onClick={() => onChoose({
            preset: "custom",
            from: new Date(from).toISOString(),
            to: new Date(to).toISOString(),
          })}
        >
          <Icon name="checkSmall" size={14} /> Apply custom range
        </button>
      </div>
    </>
  );
}

function DrillHeader({ crumb, onBack }: { crumb: string; onBack: () => void }): JSX.Element {
  return (
    <div className="cp-pop-drill-head">
      <button type="button" onClick={onBack} title="Back" aria-label="Back">
        <Icon name="chevronLeft" size={15} stroke={1.9} />
      </button>
      <span dir="ltr">{crumb}</span>
    </div>
  );
}

function DrillRow({
  name,
  hasChildren,
  depth = 0,
  onOpen,
}: {
  name: string;
  hasChildren?: boolean;
  depth?: number;
  onOpen: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="cp-pop-drill-row"
      style={depth ? { paddingLeft: 14 + depth * 16 } : undefined}
      onClick={onOpen}
    >
      <Icon name="folder" size={15} className="cp-pop-folder" />
      <span>{name}</span>
      {hasChildren && <Icon name="chevronRight" size={14} stroke={1.9} className="cp-pop-chevron" />}
    </button>
  );
}

/**
 * The folder tree, for either store.
 *
 * One component for SharePoint and OneDrive because below the root they are the
 * same thing — a drive and the folders in it. The only difference is where the
 * first level comes from, and the server settles that.
 */
function FolderTree({
  source,
  siteId,
  treeLabel,
  emptyMessage,
  onChoose,
}: {
  source: KnowledgeTreeSource;
  siteId?: string;
  treeLabel: string;
  emptyMessage: string;
  onChoose: (item: SharePointTreeItem) => void;
}): JSX.Element {
  const roots = useKnowledgeTree(source, siteId);
  const items = roots.data?.items ?? [];
  return (
    <div role="tree" aria-label={`Folders in ${treeLabel}`}>
      {roots.isLoading && (
        <p className="cp-pop-hint">
          {source === "oneDrive" ? "Loading folders…" : "Loading libraries…"}
        </p>
      )}
      {roots.isError && (
        <p className="cp-pop-hint">
          Could not load {source === "oneDrive" ? "your OneDrive" : "this site"}.
        </p>
      )}
      {!roots.isLoading && !roots.isError && items.length === 0 && (
        <p className="cp-pop-hint">{emptyMessage}</p>
      )}
      {items.map((item) => (
        <FolderTreeNode
          key={`${item.driveId}:${item.id}`}
          source={source}
          siteId={siteId}
          item={item}
          depth={0}
          autoExpand={items.length === 1}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
}

/**
 * One folder. Clicking the row commits this folder as the scope; the chevron
 * expands it, so walking deeper and choosing where you are never fight for the
 * same target.
 *
 * A folder that is the only one at its level starts out open. That is read once,
 * at mount — by then the level has loaded, so "am I the only one here?" is
 * already answered — and it is a starting position rather than a rule: the
 * toggle still wins afterwards.
 */
function FolderTreeNode({
  source,
  siteId,
  item,
  depth,
  autoExpand,
  onChoose,
}: {
  source: KnowledgeTreeSource;
  siteId?: string;
  item: SharePointTreeItem;
  depth: number;
  autoExpand: boolean;
  onChoose: (item: SharePointTreeItem) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(
    autoExpand && item.hasChildren && depth < AUTO_EXPAND_DEPTH,
  );
  const children = useKnowledgeTree(
    source,
    siteId,
    item.driveId,
    item.kind === "folder" ? item.id : undefined,
    expanded && item.hasChildren,
  );
  const childItems = children.data?.items ?? [];

  return (
    <div>
      <div className="cp-pop-tree-row" role="treeitem" aria-expanded={item.hasChildren ? expanded : undefined}>
        <button
          type="button"
          className="cp-pop-drill-row"
          style={{ paddingLeft: 14 + depth * 16 }}
          onClick={() => onChoose(item)}
        >
          <Icon name="folder" size={15} className="cp-pop-folder" />
          <span>{item.name}</span>
        </button>
        {item.hasChildren && (
          <button
            type="button"
            className={`cp-pop-tree-toggle${expanded ? " is-open" : ""}`}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${item.name}`}
            onClick={() => setExpanded((current) => !current)}
          >
            <Icon name="chevronRight" size={14} stroke={1.9} />
          </button>
        )}
      </div>
      {expanded && (
        <div role="group">
          {children.isLoading && <p className="cp-pop-hint">Loading…</p>}
          {childItems.map((child) => (
            <FolderTreeNode
              key={`${child.driveId}:${child.id}`}
              source={source}
              siteId={siteId}
              item={child}
              depth={depth + 1}
              autoExpand={childItems.length === 1}
              onChoose={onChoose}
            />
          ))}
          {!children.isLoading && childItems.length === 0 && (
            <p className="cp-pop-hint">No subfolders here.</p>
          )}
        </div>
      )}
    </div>
  );
}
